import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import { format } from 'date-fns';
import { calculateDeadline } from '../../utils/helpers';

export const DeadlineCard = ({ 
  title, 
  description, 
  startDate, 
  daysAllowed, 
  legalBasis,
  category,
  projectId,
  onEscalate 
}) => {
  const { deadlineDate, daysRemaining, isOverdue, severity } = calculateDeadline(startDate, daysAllowed);

  const categoryIcons = {
    'Procurement': FiIcons.FiShoppingCart,
    'Reporting': FiIcons.FiFileText,
    'Financial': FiIcons.FiDollarSign,
    'Contract': FiIcons.FiFile,
    'Audit': FiIcons.FiShield
  };

  const CategoryIcon = categoryIcons[category] || FiIcons.FiClock;

  return (
    <div 
      className={clsx(
        "p-4 rounded-lg border-l-4 transition-all hover:shadow-md",
        isOverdue 
          ? "bg-red-50 border-l-red-600 border-red-200" 
          : severity === 'critical'
          ? "bg-orange-50 border-l-orange-600 border-orange-200"
          : severity === 'warning'
          ? "bg-amber-50 border-l-amber-500 border-amber-200"
          : "bg-slate-50 border-l-grn-navy border-slate-200"
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          <div className={clsx(
            "p-2 rounded-lg shrink-0",
            isOverdue 
              ? "bg-red-100 text-red-600"
              : severity === 'critical'
              ? "bg-orange-100 text-orange-600"
              : severity === 'warning'
              ? "bg-amber-100 text-amber-600"
              : "bg-blue-100 text-grn-navy"
          )}>
            <SafeIcon icon={CategoryIcon} className="text-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-900 text-sm leading-tight mb-1">
              {title}
            </h3>
            <p className="text-xs text-slate-600 line-clamp-2">
              {description}
            </p>
          </div>
        </div>

        {isOverdue && (
          <span className="px-2 py-1 bg-red-600 text-white text-[10px] font-bold rounded-full animate-pulse shrink-0">
            BREACH
          </span>
        )}
      </div>

      {/* Project Reference */}
      {projectId && (
        <div className="mb-3 flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiBriefcase} className="text-xs text-slate-400" />
          <span className="text-xs text-slate-500 font-mono">{projectId}</span>
        </div>
      )}

      {/* Deadline Info */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
            Due Date
          </p>
          <p className="text-sm font-bold text-slate-800">
            {deadlineDate}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
            {isOverdue ? 'Overdue By' : 'Days Remaining'}
          </p>
          <p className={clsx(
            "text-2xl font-black",
            isOverdue 
              ? "text-red-700"
              : severity === 'critical'
              ? "text-orange-700"
              : severity === 'warning'
              ? "text-amber-700"
              : "text-grn-navy"
          )}>
            {isOverdue ? `+${Math.abs(daysRemaining)}` : daysRemaining}
            <span className="text-sm ml-1">days</span>
          </p>
        </div>
      </div>

      {/* Legal Basis */}
      <div className="mb-3 p-2 bg-white/50 rounded border border-slate-200">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiBook} className="text-xs text-slate-400" />
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
            Legal Basis:
          </span>
          <span className="text-xs font-mono text-slate-700 font-bold">
            {legalBasis}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button className="flex-1 px-3 py-1.5 bg-white border border-slate-300 rounded text-xs font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-1">
          <SafeIcon icon={FiIcons.FiEye} />
          View Details
        </button>
        {(isOverdue || severity === 'critical') && (
          <button 
            onClick={onEscalate}
            className="flex-1 px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-1"
          >
            <SafeIcon icon={FiIcons.FiAlertTriangle} />
            Escalate
          </button>
        )}
      </div>
    </div>
  );
};